import { CohortCriteriaDSL, CriteriaGroup, CriteriaFact } from "./types";

// Validation configuration
const MAX_NESTING_DEPTH = 6;

export interface ValidationError {
  path: string;
  message: string;
  severity: "error" | "warning";
}

export interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
}

/**
 * Type guard to check if a node is a CriteriaFact
 */
function isCriteriaFact(node: any): node is CriteriaFact {
  return node && typeof node === "object" && "fact" in node && "cmp" in node;
}

/**
 * Type guard to check if a node is a CriteriaGroup
 */
function isCriteriaGroup(node: any): node is CriteriaGroup {
  return node && typeof node === "object" && "op" in node && "children" in node;
}

/**
 * Validate a single criterion (fact)
 */
function validateCriterion(
  fact: CriteriaFact,
  path: string
): ValidationError[] {
  const errors: ValidationError[] = [];

  // Validate fact is present
  if (!fact.fact || typeof fact.fact !== "string") {
    errors.push({
      path,
      message: "Criterion must have a valid field",
      severity: "error",
    });
  }

  // Validate operator is present
  if (!fact.cmp || typeof fact.cmp !== "string") {
    errors.push({
      path,
      message: "Criterion must have a valid operator field",
      severity: "error",
    });
  }

  // Validate value for operators that require it
  const operatorsThatRequireValue = [
    "=",
    "!=",
    ">",
    ">=",
    "<",
    "<=",
    "between",
    "in",
    "not_in",
    "contains",
  ];
  if (operatorsThatRequireValue.includes(fact.cmp)) {
    if (fact.value === undefined || fact.value === null) {
      errors.push({
        path,
        message: `Operator '${fact.cmp}' requires a value`,
        severity: "error",
      });
    }
  }

  // Validate 'between' operator has array with 2 values
  if (fact.cmp === "between") {
    if (!Array.isArray(fact.value) || fact.value.length !== 2) {
      errors.push({
        path,
        message: "Operator 'between' requires an array with exactly 2 values",
        severity: "error",
      });
    }
  }

  // Validate 'in' and 'not_in' operators have array or comma-separated string
  if (fact.cmp === "in" || fact.cmp === "not_in") {
    if (!Array.isArray(fact.value) && typeof fact.value !== "string") {
      errors.push({
        path,
        message: `Operator '${fact.cmp}' requires an array or comma-separated string`,
        severity: "error",
      });
    }
  }

  // Validate tolerance is a positive number if specified
  if (fact.tolerance !== undefined && fact.tolerance !== null) {
    if (typeof fact.tolerance !== "number" || fact.tolerance < 0) {
      errors.push({
        path,
        message: "Tolerance must be a positive number",
        severity: "error",
      });
    }
  }

  // Validate time window if specified
  if (fact.window) {
    if (typeof fact.window !== "object") {
      errors.push({
        path,
        message: "Time window must be an object with 'days' or 'hours'",
        severity: "error",
      });
    } else {
      const { days, hours } = fact.window;
      if (days !== undefined && (typeof days !== "number" || days <= 0)) {
        errors.push({
          path,
          message: "Time window 'days' must be a positive number",
          severity: "error",
        });
      }
      if (hours !== undefined && (typeof hours !== "number" || hours <= 0)) {
        errors.push({
          path,
          message: "Time window 'hours' must be a positive number",
          severity: "error",
        });
      }
    }
  }

  return errors;
}

/**
 * Validate a group recursively
 */
function validateGroup(
  group: CriteriaGroup,
  path: string,
  depth: number = 0
): ValidationError[] {
  const errors: ValidationError[] = [];

  // Check max nesting depth
  if (depth > MAX_NESTING_DEPTH) {
    errors.push({
      path,
      message: `Maximum nesting depth of ${MAX_NESTING_DEPTH} exceeded`,
      severity: "error",
    });
    return errors; // Stop recursion to prevent stack overflow
  }

  // Validate operator
  if (!group.op || !["AND", "OR", "NOT"].includes(group.op)) {
    errors.push({
      path,
      message: "Group must have a valid operator (AND, OR, or NOT)",
      severity: "error",
    });
  }

  // Validate children array exists
  if (!Array.isArray(group.children)) {
    errors.push({
      path,
      message: "Group must have a 'children' array",
      severity: "error",
    });
    return errors; // Can't continue validation without children
  }

  // Validate group has at least one child
  if (group.children.length === 0) {
    errors.push({
      path,
      message: "Group must have at least one child criterion or sub-group",
      severity: "error",
    });
  }

  // Special validation for NOT groups (negate=true)
  if (group.negate === true) {
    if (group.children.length === 0) {
      errors.push({
        path,
        message: "NOT groups cannot be empty",
        severity: "error",
      });
    }
  }

  // Recursively validate children
  group.children.forEach((child: any, index: number) => {
    const childPath = `${path}.children[${index}]`;

    if (isCriteriaFact(child)) {
      errors.push(...validateCriterion(child, childPath));
    } else if (isCriteriaGroup(child)) {
      errors.push(...validateGroup(child, childPath, depth + 1));
    } else {
      errors.push({
        path: childPath,
        message: "Child must be either a criterion or a group",
        severity: "error",
      });
    }
  });

  return errors;
}

/**
 * Validate the complete DSL structure
 */
export function validateDSL(dsl: CohortCriteriaDSL): ValidationResult {
  const errors: ValidationError[] = [];

  // Validate scope
  if (!dsl.scope || !["single-study", "multi-study"].includes(dsl.scope)) {
    errors.push({
      path: "scope",
      message: "DSL must have a valid scope ('single-study' or 'multi-study')",
      severity: "error",
    });
  }

  // Validate studyId for single-study scope
  if (dsl.scope === "single-study" && !dsl.studyId) {
    errors.push({
      path: "studyId",
      message: "Single-study scope requires a studyId",
      severity: "error",
    });
  }

  // Validate root group
  if (!dsl.root) {
    errors.push({
      path: "root",
      message: "DSL must have a root group",
      severity: "error",
    });
  } else if (!isCriteriaGroup(dsl.root)) {
    errors.push({
      path: "root",
      message: "Root must be a valid CriteriaGroup",
      severity: "error",
    });
  } else {
    errors.push(...validateGroup(dsl.root, "root", 0));
  }

  // Validate output configuration
  if (!dsl.output) {
    errors.push({
      path: "output",
      message: "DSL must have an output configuration",
      severity: "error",
    });
  } else {
    if (
      !dsl.output.dedupeBy ||
      !["participant", "study_participant"].includes(dsl.output.dedupeBy)
    ) {
      errors.push({
        path: "output.dedupeBy",
        message:
          "Output must have a valid dedupeBy field ('participant' or 'study_participant')",
        severity: "error",
      });
    }

    if (!Array.isArray(dsl.output.select)) {
      errors.push({
        path: "output.select",
        message: "Output must have a 'select' array",
        severity: "error",
      });
    }
  }

  return {
    valid: errors.filter((e) => e.severity === "error").length === 0,
    errors,
  };
}

/**
 * Get nesting depth of a group
 */
export function getGroupDepth(group: CriteriaGroup): number {
  let maxDepth = 0;

  for (const child of group.children) {
    if (isCriteriaGroup(child)) {
      maxDepth = Math.max(maxDepth, 1 + getGroupDepth(child));
    }
  }

  return maxDepth;
}

/**
 * Count total criteria (facts) in a DSL tree
 */
export function countCriteria(group: CriteriaGroup): number {
  let count = 0;

  for (const child of group.children) {
    if (isCriteriaFact(child)) {
      count++;
    } else if (isCriteriaGroup(child)) {
      count += countCriteria(child);
    }
  }

  return count;
}

/**
 * Count total groups in a DSL tree (including root)
 */
export function countGroups(group: CriteriaGroup): number {
  let count = 1; // Count the current group

  for (const child of group.children) {
    if (isCriteriaGroup(child)) {
      count += countGroups(child);
    }
  }

  return count;
}
